import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  useBulkDuplicateLines,
  useDeleteCartLine,
} from "@/api/queries";
import { ConfirmDialog } from "@/components/admin/ConfirmDialog";
import { useCartSelection } from "@/components/cart/CartSelectionContext";
import { getOperationTone } from "@/lib/operationTone";

/**
 * Contextual selection toolbar — replaces the section header while the
 * section has ``selectedCount > 0``.
 *
 * Duplikovat / Odstranit / × are the universal actions. The declarative
 * per-process "bulk apply" dropdown machinery (``cartBulkFields``) was
 * removed as dead code: no module ever declared a field after the
 * registry became metadata-driven, so the menus never rendered.
 * ``Duplikovat`` posts to ``POST /cart/lines/bulk/duplicate`` via
 * :func:`useBulkDuplicateLines`.
 */
export interface BulkBarLine {
  id: number;
  material_code: string | null;
}

export function BulkBar({
  opLines,
  processSlug,
  toneColor,
}: {
  opLines: BulkBarLine[];
  processSlug: string;
  /** Tone source resolved by ``operationToneColor`` — the process's
   *  operation-category hex, or its Tailwind colour token. */
  toneColor?: string | null;
}): JSX.Element | null {
  const opLineIds = useMemo(() => opLines.map((l) => l.id), [opLines]);
  const { t } = useTranslation("cart");
  const { selected, clearOperation, countInOperation } = useCartSelection();
  const duplicate = useBulkDuplicateLines();
  const deleteLine = useDeleteCartLine();
  const tone = getOperationTone(toneColor);

  const [toast, setToast] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const selectedInSection = useMemo(
    () => opLineIds.filter((id) => selected.has(id)),
    [opLineIds, selected],
  );

  const count = countInOperation(opLineIds);
  if (count === 0) return null;

  const runBulkDuplicate = async () => {
    try {
      const res = await duplicate.mutateAsync({ line_ids: selectedInSection });
      if (res.failed.length === 0) {
        setToast(
          t("bulkBar.toast.allDuplicated", {
            defaultValue: "{{count}} variant duplikováno",
            count: res.created,
          }),
        );
      } else {
        setToast(
          t("bulkBar.toast.partialDuplicate", {
            defaultValue: "{{ok}} duplikováno, {{fail}} selhalo",
            ok: res.created,
            fail: res.failed.length,
          }),
        );
      }
      clearOperation(opLineIds);
    } catch (e) {
      setToast(
        t("bulkBar.toast.error", {
          defaultValue: "Hromadná úprava selhala: {{message}}",
          message: (e as Error).message,
        }),
      );
    }
  };

  const runBulkDelete = async () => {
    setConfirmDelete(false);
    const results = await Promise.allSettled(
      selectedInSection.map((id) => deleteLine.mutateAsync(id)),
    );
    const ok = results.filter((r) => r.status === "fulfilled").length;
    const fail = results.length - ok;
    clearOperation(opLineIds);
    setToast(
      fail === 0
        ? t("bulkBar.toast.allDeleted", {
            defaultValue: "{{count}} variant odstraněno",
            count: ok,
          })
        : t("bulkBar.toast.partialDelete", {
            defaultValue: "{{ok}} odstraněno, {{fail}} selhalo",
            ok,
            fail,
          }),
    );
  };

  const isBusy = duplicate.isPending || deleteLine.isPending;

  return (
    <div
      className="flex min-h-[4.25rem] flex-wrap items-center gap-3 border-b px-4 py-2 text-sm"
      style={{ background: tone.bg, borderColor: tone.border }}
      data-testid={`cart-bulk-bar-${processSlug}`}
    >
      <span
        className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[12px] font-semibold text-white"
        style={{ background: tone.dot }}
        data-testid={`cart-bulk-bar-${processSlug}-count`}
      >
        {count}
      </span>
      <span style={{ color: tone.ink }}>
        {t("bulkBar.selectedHeader", {
          defaultValue: "vybráno — upravit:",
        })}
      </span>

      <span className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={runBulkDuplicate}
          disabled={isBusy}
          className="rounded-md border border-border bg-surface px-3 py-1 text-xs text-ink-2 hover:bg-bg disabled:cursor-not-allowed disabled:opacity-50"
          data-testid={`cart-bulk-bar-${processSlug}-duplicate`}
        >
          {t("bulkBar.duplicate", { defaultValue: "Duplikovat" })}
        </button>

        <button
          type="button"
          onClick={() => setConfirmDelete(true)}
          disabled={isBusy}
          className="rounded-md border border-danger-border bg-surface px-3 py-1 text-xs text-danger hover:bg-danger-bg disabled:cursor-not-allowed disabled:opacity-50"
          data-testid={`cart-bulk-bar-${processSlug}-delete`}
        >
          {t("bulkBar.delete", { defaultValue: "Odstranit" })}
        </button>
      </span>

      <button
        type="button"
        onClick={() => clearOperation(opLineIds)}
        disabled={isBusy}
        className="ml-auto flex h-6 w-6 items-center justify-center rounded-md text-ink-3 hover:bg-surface hover:text-ink-2 disabled:cursor-not-allowed disabled:opacity-50"
        aria-label={t("bulkBar.closeSelection", {
          defaultValue: "Zavřít výběr",
        })}
        data-testid={`cart-bulk-bar-${processSlug}-close`}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M6 6l12 12M18 6L6 18"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {toast && (
        <div
          className="basis-full text-[11px] text-ink-2"
          role="status"
          aria-live="polite"
          data-testid={`cart-bulk-bar-${processSlug}-toast`}
        >
          {toast}
          <button
            type="button"
            onClick={() => setToast(null)}
            className="ml-2 text-ink-3 underline"
          >
            ×
          </button>
        </div>
      )}
      <ConfirmDialog
        open={confirmDelete}
        title={t("bulkBar.confirmDeleteTitle", {
          defaultValue: "Odstranit vybrané varianty?",
        })}
        description={
          <p>
            {t("bulkBar.confirmDeleteDescription", {
              defaultValue:
                "Odstraním {{count}} variant z této operace. Tuto akci nelze vrátit zpět.",
              count: selectedInSection.length,
            })}
          </p>
        }
        confirmLabel={t("bulkBar.confirmDeleteCta", {
          defaultValue: "Odstranit",
        })}
        cancelLabel={t("bulkBar.confirmDeleteCancel", {
          defaultValue: "Zrušit",
        })}
        busy={deleteLine.isPending}
        onConfirm={runBulkDelete}
        onClose={() => setConfirmDelete(false)}
        testId={`cart-bulk-bar-${processSlug}-confirm`}
      />
    </div>
  );
}
